import gsap from 'gsap'
import { forEach } from 'lodash'
import { LUTCubeLoader } from 'postprocessing'
import { Group, LoadingManager, Texture, TextureLoader } from 'three'
import { DRACOLoader, GLTF, GLTFLoader } from 'three-stdlib'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

import { BASE_PATH, DRACO_DECODERS } from '@/constants'

type Store = Map<string, GLTF | Texture>

type State = {
  cache: Store
  ready: boolean
  loaded: boolean
  loading: boolean
  compiled: boolean
  progress: number
  // eslint-disable-next-line no-unused-vars
  compile: (tasks: Promise<void>[]) => void
  complete: () => void
  load: () => void
}

type LoaderAsset = {
  name: string
  type: string
  src: string | string[]
}

const AssetType = {
  TEXTURE: 'texture',
  GLTF: 'gltf',
  LUT: 'lut'
}

const getAssets = () => [
  {
    name: 'hero-scene',
    type: 'gltf',
    src: `${BASE_PATH}/models/hero.glb`
  },
  {
    name: 'chip-scene',
    type: 'gltf',
    src: `${BASE_PATH}/models/chip.glb`
  },
  {
    name: 'neutrino-scene',
    type: 'gltf',
    src: `${BASE_PATH}/models/neutrino.glb`
  },
  {
    name: 'tunnel-scene',
    type: 'gltf',
    src: `${BASE_PATH}/models/tunnel.glb`
  },
  {
    name: 'lines-medium',
    type: 'texture',
    src: `${BASE_PATH}/textures/hero-lines-medium-min.jpg`
  },
  {
    name: 'lines-bold',
    type: 'texture',
    src: `${BASE_PATH}/textures/hero-lines-bold-min.jpg`
  },
  {
    name: 'lines-thin',
    type: 'texture',
    src: `${BASE_PATH}/textures/hero-lines-thin-min.jpg`
  },
  {
    name: 'tunnel-circles',
    type: 'texture',
    src: `${BASE_PATH}/textures/tunnel-circles-min.jpg`
  },
  {
    name: 'tunnel-circuits',
    type: 'texture',
    src: `${BASE_PATH}/textures/tunnel-circuits-min.jpg`
  },
  {
    name: 'tunnel-lines',
    type: 'texture',
    src: `${BASE_PATH}/textures/tunnel-lines-min.jpg`
  },
  {
    name: 'perlin-noise',
    type: 'texture',
    src: `${BASE_PATH}/textures/perlin-datatexture.png`
  },
  {
    name: 'simplex-noise',
    type: 'texture',
    src: `${BASE_PATH}/textures/noise.png`
  },
  {
    name: 'advect-noise',
    type: 'texture',
    src: `${BASE_PATH}/textures/advect.png`
  },
  {
    name: 'lut-cube',
    type: 'lut',
    src: `${BASE_PATH}/textures/lut-R04.cube`
  },
  {
    name: 'lut',
    type: 'texture',
    src: `${BASE_PATH}/textures/lut.png`
  }
]

const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath(DRACO_DECODERS)

const getLoader = (asset: LoaderAsset, manager: LoadingManager) => {
  let loader
  switch (asset.type) {
    case AssetType.TEXTURE:
      loader = new TextureLoader(manager)
      break
    case AssetType.LUT:
      loader = new LUTCubeLoader(manager)
      break
    case AssetType.GLTF:
      loader = new GLTFLoader(manager)
      loader.setDRACOLoader(dracoLoader)
      break
  }
  return loader
}

const useLoader = create(
  subscribeWithSelector<State>((set, get) => ({
    cache: new Map(),
    ready: false,
    loaded: false,
    loading: false,
    compiled: false,
    progress: 0,
    load: () => {
      console.log('loading::start')
      console.time('loading::time')
      new Promise<Store>((resolve) => {
        const manager = new LoadingManager()
        manager.onStart = () => set({ loading: true })
        manager.onProgress = (_, itemsLoaded, itemsTotal) =>
          set({ progress: (itemsLoaded / itemsTotal) * 0.9 })
        manager.onLoad = () => {
          set({ loading: false, loaded: true })
          resolve(get().cache)
          console.timeEnd('loading::time')
          console.log('loading::complete')
        }

        forEach(getAssets(), (asset) => {
          const loader = getLoader(asset, manager)
          loader?.load(asset.src as string & readonly string[], (result) => {
            get().cache.set(asset.name, result)
          })
        })
      })
    },
    compile: async (tasks = []) => {
      console.log('compile::start', tasks)
      console.time('compile::time')
      set({ compiled: false })
      await Promise.all(tasks)
      gsap.delayedCall(0, () => {
        set({ compiled: true })
      })
      console.timeEnd('compile::time')
      console.log('compile::complete')
    },
    complete: () => {
      set({ ready: true })
    }
  }))
)

const selectFromCache = <T>(key: string) => useLoader.getState().cache.get(key) as T

const selectObjectByName = <T>(key: string, root: Group) => {
  return root.getObjectByName(key) as T
}

export { selectFromCache, selectObjectByName, useLoader }
